// ADR 11.9.26i §3 — where a browser's device token lives.
//
// A sibling of `tokenStore.ts`, never a field of it. The phones keep the token
// in `device.json` beside `tokens.json` so that logout does not destroy it
// (7.8.26c D5), and the same trap is restated here: a device identity folded
// into the auth store is cleared on every sign-out, so every sign-out costs a
// solve and mints a fresh anonymous subject — the churn
// `anonymous_identity_source_total` exists to measure, manufactured by the
// client. The two keys share one `localStorage` and have different lifetimes;
// the file boundary is what keeps them apart. `signOutLocally()` does not
// know this module exists (AC5).
//
// Every access is wrapped: a browser set to block site data, or a thumbnail
// capture, throws on `localStorage` and this module reads `null` and writes
// nothing. The token then lives in `cached` for the session — so the request
// that enrolled still carries it (AC6) — and is re-minted next session.
//
// The lifetime questions are `rust-core`'s (`device_token/mod.rs`) and reach
// here through the wasm shims. Nothing in this file, or anywhere under
// `web/src`, reads `exp` off a token with a `split('.')` — 9.9.26u's rule, and
// AC7's assertion.
//
// ADR 25.9.26f §3 — through `import('../wasm')`, never statically: `api/http.ts`
// imports this module for `readDeviceToken`, and `http.ts` is on the boot graph,
// so a static edge would hold the signed-out first paint for the wasm download.
// Only `deviceTokenLifetime` needs the facade, and its one caller already awaits.

/**
 * The one key this module writes. `.v1` for the same reason
 * `lingara.web.refresh.v1` carries it: a future change to the stored shape
 * refuses an old value rather than reading it as a new one.
 */
export const DEVICE_TOKEN_KEY = 'lingara.web.device.v1'

/** Where a stored token stands at `nowUnix`. */
export type DeviceTokenLifetime = 'live' | 'due' | 'expired'

let cached: string | null = null

/** The stored token, or `null` — from memory first, then storage. */
export function readDeviceToken(): string | null {
  if (cached !== null) return cached
  try {
    cached = window.localStorage.getItem(DEVICE_TOKEN_KEY)
  } catch {
    cached = null
  }
  return cached
}

/** Adopt a freshly minted or renewed token. A failed persist is not an error. */
export function writeDeviceToken(token: string): void {
  cached = token
  try {
    window.localStorage.setItem(DEVICE_TOKEN_KEY, token)
  } catch {
    // Kept in `cached` for the session; the next session re-enrols.
  }
}

/** Forget the token — the server refused it (§5), or a test is resetting. */
export function clearDeviceToken(): void {
  cached = null
  try {
    window.localStorage.removeItem(DEVICE_TOKEN_KEY)
  } catch {
    // Nothing to remove from a storage that never accepted a write.
  }
}

/** Seconds since the epoch — the `now` the wasm predicates take, never read inside them. */
export function nowUnix(): number {
  return Math.floor(Date.now() / 1000)
}

/**
 * `expired` at or past `exp`; `due` inside the 30-day renewal window; `live`
 * otherwise. An unparseable token is `due` and never `expired`, exactly as
 * `is_due` / `is_expired` answer on the phones.
 */
export async function deviceTokenLifetime(token: string, now: number): Promise<DeviceTokenLifetime> {
  const { deviceTokenDue, deviceTokenExpired } = await import('../wasm')
  if (deviceTokenExpired(token, now)) return 'expired'
  return deviceTokenDue(token, now) ? 'due' : 'live'
}
